import { Fragment, useCallback, useEffect, useMemo } from 'react'
import { SwitchButtonSmall, TabItems } from '../../../../../../../../baseComponents/Button/SwitchButton'
import TableWithoutVirtualListing from '../../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { DeleteRow } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import { CheckboxMargin } from '../../../../../../../../forms/Checkbox'
import { CustomSelectSectionsSearchbar } from '../../../../../../../../forms/CustomSelect/CustomSelectSections'
import { CustomSelectSectionItems } from '../../../../../../../../forms/CustomSelect/CustomSelectSections/types'
import InputQuantity from '../../../../../../../../forms/Input/InputQuantity'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { SellVariablePayload } from '../../../../../../../../utils/reducers/company/sellVariables'
import { excludeProductAddonId, parseScaleValue, parseSellVariables, sortProductAddons } from '../../../../../../../../specificComponents/Product/ProductLayout/Tabs/ProductAddon/ModalUpdateProductAddons/functions'
import SearchBarProduct from '../../../../../../../../specificComponents/Product/SearchBarProduct'
import { ProductExtendedTyShort } from '../../../../../../../../specificComponents/Product/types'
import { filterIfAlreadyExists } from './functions'
import { CategoryAddonDict, CategoryAddonTy, DictCategoryAddonContentTy, CategoryAddonWithLang } from '../types'
import CategoryAddonSortButton from './CategoryAddonsSortButton'
import { ComponentWithPermissions } from '../../../../../../../../baseComponents/CreateRoutering/types'
import { getConfigParam } from '../../../../../../../../utils/reducers/getters'
import { ProductAddonTypeTyKeys } from '@teinor/erp/types/company/config/parameter'
import {CustomRenderString} from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'

interface CategoryAddonsBaseProps  {
    CategoryId?: number
    categoryAddons: CategoryAddonDict | null
    setCategoryAddons: React.Dispatch<React.SetStateAction<CategoryAddonDict | null>>
    sellVariables: SellVariablePayload
}

type CategoryAddonsProps = CategoryAddonsBaseProps & ComponentWithPermissions  & {
    productAddonType: ProductAddonTypeTyKeys | null
    typeCategoryAddon: CategoryAddonTy
    setTypeCategoryAddon: React.Dispatch<React.SetStateAction<CategoryAddonTy>>
}

const CategoryAddons: React.FC<CategoryAddonsProps> = ({ productAddonType, categoryAddons, typeCategoryAddon, setTypeCategoryAddon, userPermissions, CategoryId, ...logic}) => {
    const { t } = useTwinTranslation()

    const myProductAddonType = productAddonType || getConfigParam('productAddonType')

    const dictHeaderProductAddons: Record<ProductAddonTypeTyKeys, JSX.Element> = {
        0: <SwitchProductTypeAddon typeCategoryAddon={typeCategoryAddon} setTypeCategoryAddon={setTypeCategoryAddon} />,
        1: <CustomRenderString value={t('categoryAddonsSells', 'Complementos de venta por categoría')} />,
        2: <CustomRenderString value={t('categoryAddonsBuys', 'Complementos de compra por categoría')} />,
        3: <CustomRenderString value={t('categoryAddons', 'Complementos de categoría')} />,
    }
    const typeCategoryAddonContent = useMemo(() =>{
        const dictTypeCategoryAddon: Record<CategoryAddonTy, DictCategoryAddonContentTy> = {
            0: { 
                url: '/api/app/product/category/categoryAddon/getAllCategoryAddons',
                urlUpdateOrder: '/api/app/product/category/categoryAddon/updateCategoryAddonOrder',
                typeData: 'dataSell',
                typeId: 'idsSell'
            },
            1: { 
                url: '/api/app/product/category/categoryAddonBuy/getAllCategoryAddonsBuy',
                urlUpdateOrder: '/api/app/product/category/categoryAddonBuy/updateCategoryAddonBuyOrder',
                typeData: 'dataBuy',
                typeId: 'idsBuy' 
            }
        }
        return dictTypeCategoryAddon[typeCategoryAddon]
    }, [typeCategoryAddon])
    const { addCategoryAddonRel, deleteCategoryAddonRel, myOnChange, myOnChangeCustomSelect, memorizedItems, getDataFromStart } = useCategoryAddonsLogic({ categoryAddons, typeCategoryAddonContent, CategoryId, ...logic })

    
    const columns: ColumnTableSchema<CategoryAddonWithLang> = [
        {
            id: 'ProductAddonId',
            dataKey: 'ProductAddonId',
            label: t('id', 'ID'),
            width: 75
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
        },
        {
            id: 'ScaleByProductId',
            dataKey: 'ScaleByProductId',
            label: t('type', 'Tipo'),
            customRender: (parameterValue, allRowData) => <CustomSelectSectionsSearchbar value={parseScaleValue(allRowData)} label='' items={excludeProductAddonId(memorizedItems, allRowData?.ProductAddonId)} className='w-100' fieldName='name' onChange={(val) => myOnChangeCustomSelect(allRowData?.rowIndex, val)} sortFunction={(items) => sortProductAddons(items, allRowData?.id)} />
        },
        {
            id: 'multiplyByWeight',
            dataKey: 'multiplyByWeight',
            label: t('mByWeight', 'M. por peso'),
            toolTip: t('multiplyByWeight', 'Multiplicar por peso'),
            customRender: (value, allRowData) => <div className='flex flex-auto justify-center'> <CheckboxMargin checked={String(value) === 'true' ? true : false} onChange={(val) => myOnChange(allRowData?.rowIndex, val, 'multiplyByWeight')} label={''} /></div>,
        },
        {
            id: 'createDefault',
            dataKey: 'createDefault',
            label: t('create', 'Crear'),
            customRender: (value, allRowData) => <div className='flex flex-auto justify-center'> <CheckboxMargin checked={String(value) === 'true' ? true : false} onChange={(val) => myOnChange(allRowData?.rowIndex, val, 'createDefault')} label={''} /></div>,
            width: 50
        },
        {
            id: 'isParent',
            dataKey: 'isParent',
            label: t('isParent', 'Es padre'),
            customRender: (value, allRowData) => <div className='flex flex-auto justify-center'> <CheckboxMargin checked={String(value) === 'true' ? true : false} onChange={(val) => myOnChange(allRowData?.rowIndex, val, 'isParent')} label={''} /></div>,
            width: 100
        },
        {
            id: 'scaleQuantity',
            dataKey: 'scaleQuantity',
            label: t('quantity', 'Cantidad'),
            customRender: (value, allRowData) => <InputQuantity typeQuantity={''} value={value ? value : ''} onChange={(val) => myOnChange(allRowData?.rowIndex, val, 'scaleQuantity')} />
        },
        {
            id: 'delete',
            dataKey: 'CategoryId',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteCategoryAddonRel(allRowData.rowIndex)} />,
        }
    ]

    const children = useMemo(() => {
        return <div className='ml-auto'><CategoryAddonSortButton url={typeCategoryAddonContent.urlUpdateOrder} extraWhereParams={{ CategoryId }} urlGetData={typeCategoryAddonContent.url} {...{ userPermissions }} tableInstance={{ getTableDataFromStart: getDataFromStart, changeOffset: () => { } }} /></div>
    }, [getDataFromStart, typeCategoryAddonContent.url, typeCategoryAddonContent.urlUpdateOrder, userPermissions, CategoryId])

    return (
        <Fragment>
            <div className='flex items-center mb-10'>
                {dictHeaderProductAddons?.[myProductAddonType || 0]}
                {children}
            </div>
            <SearchBarProduct placeholder={t('searchAddons', 'Buscar complementos')} changeSelectedItems={addCategoryAddonRel} excludeIds={categoryAddons?.[typeCategoryAddonContent.typeId] || []} id={''} fieldNotIn='Product.id' name='ProductAddonId' url='/api/app/product/getAllProducts' where={{}} />
                <div className='flex-auto table_container mt-20'>
                <TableWithoutVirtualListing data={categoryAddons?.[typeCategoryAddonContent.typeData] as ProductExtendedTyShort[] || []} columns={columns} disableScroll={true} className='category_addons' />
            </div>
        </Fragment>
    )
}

interface CategoryAddonsLogicProps extends CategoryAddonsBaseProps {
    typeCategoryAddonContent: DictCategoryAddonContentTy
}

const useCategoryAddonsLogic = ({ typeCategoryAddonContent,  CategoryId, categoryAddons, setCategoryAddons, sellVariables }: CategoryAddonsLogicProps) => {
    
    const getData = useCallback(async () => {
        const data = await twinFetchPostJSON(typeCategoryAddonContent.url, { where: { CategoryId }})
        const parsed: number[] = []
        for (const key in data) {
            const id = data[key]['ProductAddonId']
            parsed.push(id)
        }
        setCategoryAddons((old) => {
            let copyOld = JSON.parse(JSON.stringify(old))
            if(!old){
                copyOld = { idsSell: [], idsBuy: [], dataSell: [], dataBuy: []}
            }
            const { idsToAdd, filteredData } = filterIfAlreadyExists(copyOld[typeCategoryAddonContent.typeId], parsed, data)
            copyOld[typeCategoryAddonContent.typeId] = [...copyOld[typeCategoryAddonContent.typeId], ...idsToAdd]
            copyOld[typeCategoryAddonContent.typeData] = [...copyOld[typeCategoryAddonContent.typeData], ...filteredData]
            return {...copyOld }
        })
    }, [setCategoryAddons, CategoryId, typeCategoryAddonContent])

    const getDataFromStart = useCallback(async () => {
        const data = await twinFetchPostJSON(typeCategoryAddonContent.url, { where: { CategoryId }})
        const parsed: number[] = []
        for (const key in data) {
            const id = data[key]['ProductAddonId']
            parsed.push(id)
        }
        setCategoryAddons((old) => {
            let copyOld = JSON.parse(JSON.stringify(old))
            if(!old){
                copyOld = { idsSell: [], idsBuy: [], dataSell: [], dataBuy: []}
            }
            copyOld[typeCategoryAddonContent.typeId] = parsed
            copyOld[typeCategoryAddonContent.typeData] = data
            return {...copyOld }
        })
    }, [setCategoryAddons, CategoryId, typeCategoryAddonContent])


    const addCategoryAddonRel = useCallback(async (Product: ProductExtendedTyShort) => {

        setCategoryAddons((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld?.[typeCategoryAddonContent.typeData].push({
                name: Product.name,
                ProductAddonId: Product.id,
                CategoryId,
                scaleQuantity: 0,
                createDefault: false,
                multiplyByWeight: false,
                isParent: false,
                ScaleByProductId: null,
                ScaleByVariableId: null
            })
            copyOld?.[typeCategoryAddonContent.typeId].push(Product.id)
            return { ...copyOld }
        })
    }, [setCategoryAddons, CategoryId, typeCategoryAddonContent])

    const deleteCategoryAddonRel = useCallback(async (rowIndex: number) => {

        setCategoryAddons((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[typeCategoryAddonContent.typeData].splice(rowIndex, 1)
            copyOld[typeCategoryAddonContent.typeId].splice(rowIndex, 1)
            return { ...copyOld }
        })

    }, [setCategoryAddons, typeCategoryAddonContent])
    const { t } = useTwinTranslation()
    const manualText = t('manual', 'Manual')
    const productText = t('products', 'Productos')
    const variableText = t('variables', 'Variables')

    const memorizedItems: CustomSelectSectionItems = useMemo(() => {

        const dictData = dictionaryComplexFromJsonArr(categoryAddons?.[typeCategoryAddonContent.typeData] || [], "ProductAddonId")
        const items: TwinDictionary = { 0: { name: manualText, value: 0, ProductAddonId: 0 } }
        return [
            { name: productText, childs: { ...items, ...dictData } as TwinDictionary },
            { name: variableText, childs: parseSellVariables(sellVariables) }
        ]
    }, [categoryAddons, manualText, sellVariables, productText, variableText, typeCategoryAddonContent])


    const myOnChange = useCallback((rowIndex: number, value: string | boolean, field: 'scaleQuantity' | 'createDefault' | 'multiplyByWeight' | 'isParent') => {

        setCategoryAddons((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[typeCategoryAddonContent.typeData][rowIndex][field] = value
            return copyOld
        })
    }, [setCategoryAddons, typeCategoryAddonContent])

    const myOnChangeCustomSelect = useCallback((rowIndex: number, value: string) => {
        const data = typeCategoryAddonContent.typeData
        setCategoryAddons((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            if (!copyOld[data][rowIndex]['scaleQuantity']) {
                copyOld[data][rowIndex]['scaleQuantity'] = 1
            }
            let valueStr = String(value)
            if (valueStr.includes('var_')) {
                const splitted = value.split('var_')
                valueStr = splitted[1]
                copyOld[data][rowIndex]['ScaleByVariableId'] = valueStr
                copyOld[data][rowIndex]['ScaleByProductId'] = null
            } else {
                copyOld[data][rowIndex]['ScaleByProductId'] = valueStr
                if (valueStr === "0") {
                    copyOld[data][rowIndex]['ScaleByProductId'] = null
                    copyOld[data][rowIndex]['scaleQuantity'] = null
                }
                copyOld[data][rowIndex]['ScaleByVariableId'] = null
            }
            return copyOld
        })
    }, [setCategoryAddons, typeCategoryAddonContent])

    useEffect(() => {
        getData()
    }, [getData])

    return { addCategoryAddonRel, deleteCategoryAddonRel, myOnChange, myOnChangeCustomSelect, memorizedItems, getDataFromStart }
}


interface SelectorProductTypeAddonProps {
    typeCategoryAddon: CategoryAddonTy
    setTypeCategoryAddon: React.Dispatch<React.SetStateAction<CategoryAddonTy>>
}

const SwitchProductTypeAddon: React.FC<SelectorProductTypeAddonProps> = ({ typeCategoryAddon, setTypeCategoryAddon }) => {
    const items: TabItems[] = [
        {
            element: <TwinTrans transKey={'sell'}>Venta</TwinTrans>
        },
        {
            element: <TwinTrans transKey={'buy'}>Compra</TwinTrans>
        },
    ]
    return (
        <Fragment>
            <div className='regular18'> <TwinTrans transKey='categoryAddons'>Complementos de categoría</TwinTrans></div>
            <SwitchButtonSmall className='ml-10' onClick={(val) => setTypeCategoryAddon(val as any)} active={typeCategoryAddon} items={items} />
        </Fragment>)
}




export default CategoryAddons